import type { QuestionGroupProps } from "../../../../../../components/Questionnaire/Section/Question/types";
import { isQuestion } from "../../../../../../components/Questionnaire/Section/Question/types";
import type { VisibilityMap } from "../../../../../../components/Questionnaire/validation/form/types";
import type { Answers } from "../../../../types";
import type { FlattenGroupResult } from "../types";
import { getPrimaryAnswer, createEmptyFlattenResult } from "../utils";
import { getPrimaryAnswerWithPrepopulation } from "./utils/prepopulation";
import { isNoCoverValue } from "../../../../../../components/Questionnaire/Section/Question/QuestionFactory/questions/PersonalisedCoverQuestion/constants";
import { snakeCase } from "../../../../../../utils";

/**
 * Flattens a personalised cover group with a shadow yes/no gateway question.
 *
 * This creates a new structure:
 * 1. A shadow question (yes/no toggle) that acts as a gateway
 * 2. A select question (from the original primary question's options) shown when toggle is "yes"
 * 3. Child questions that are shown/hidden based on the toggle state
 *
 * When the shadow answer is "no", all child questions are hidden and their answers cleared.
 * The toggle state is derived from either an existing primary answer OR the shadow answer,
 * allowing pre-populated data to show correctly.
 */
export const flattenPersonalisedCoverShadowGroup = (
  group: Partial<QuestionGroupProps>,
  answers: Answers,
  visibility: VisibilityMap,
): FlattenGroupResult => {
  const { primaryQuestion, groupQuestions } = group;
  const filteredQuestions = groupQuestions?.filter(isQuestion) ?? [];

  if (!primaryQuestion || !primaryQuestion.options) {
    return createEmptyFlattenResult(answers, visibility);
  }

  const primaryAnswer = getPrimaryAnswer(primaryQuestion, answers);
  const shadowAnswer = answers[`Shadow::${primaryQuestion.name}`] ?? "no";

  // Toggle is on if either the primary has a valid cover answer OR shadow is explicitly "yes"
  const isPrimaryYes = primaryAnswer && !isNoCoverValue(primaryAnswer);
  const isShadowYes = shadowAnswer === "yes";
  const isToggleOn = isPrimaryYes || isShadowYes;

  // Filter options to only include actual cover options (exclude "no cover" values)
  const coverOptions = primaryQuestion.options.filter(
    opt => !isNoCoverValue(opt.value),
  );

  // Create synthetic toggle question (shadow question)
  const shadowQuestion = {
    ...primaryQuestion,
    id: snakeCase(`shadow::${primaryQuestion.name}`),
    name: `Shadow::${primaryQuestion.name}`,
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ],
    display: {
      type: "personalised_cover" as const,
    },
    includeAnswer: false,
    shouldUpdate: false,
    answer: isToggleOn ? "yes" : "no",
  };

  // Create select question from primary question's options
  // This replaces the original primary question in the UI
  // If toggle is off, answer is undefined to clear any existing answer
  // If toggle is on, retain existing primary answer (if its not a no cover value)
  const selectQuestionAnswer =
    isToggleOn && primaryAnswer && !isNoCoverValue(primaryAnswer)
      ? primaryAnswer
      : undefined;

  const selectQuestion = {
    ...primaryQuestion,
    primaryText: "Please select your cover",
    secondaryText: undefined,
    options: coverOptions,
    display: {
      type: "select" as const,
    },
    answer: selectQuestionAnswer,
    suggested: false,
    includeAnswer: true,
  };

  // Add the select question at the start of the group questions
  const filteredQuestionsWithSelect = [selectQuestion, ...filteredQuestions];

  // Map visibility for child questions and the select question
  const visibilityByQuestionName = { ...visibility };

  const questionsWithVisibility = filteredQuestionsWithSelect.map(q => {
    visibilityByQuestionName[q.name] = isToggleOn ? !!q.visible : false;
    // Skip includeAnswer for selectQuestion - let visibility map control it
    if (q.name === primaryQuestion.name) return q;
    return { ...q, includeAnswer: isToggleOn ? !!q.visible : false };
  });

  return {
    questions: [shadowQuestion, ...questionsWithVisibility],
    groups: {
      [shadowQuestion.name]: {
        kind: "shadow_personalised_cover",
        names: questionsWithVisibility.map(q => q.name),
      },
    },
    visibility: visibilityByQuestionName,
    answers: {
      ...answers,
      [selectQuestion.name]: isToggleOn ? primaryAnswer || "" : "",
    },
  };
};

/**
 * personalised_cover_question_group
 * Functions in a similar way to the yesNoToggleGroup, but group questions are displayed visually as 'children' of the primary question
 * (without technically being nested).
 */
export const flattenPersonalisedCoverGroup = (
  group: Partial<QuestionGroupProps>,
  answers: Answers,
  visibility: VisibilityMap,
): FlattenGroupResult => {
  const { primaryQuestion, groupQuestions } = group;
  const filteredQuestions = groupQuestions?.filter(isQuestion) ?? [];

  // If the primary question is missing, or all the questions are hidden, return an empty flatten group
  if (!primaryQuestion) {
    return createEmptyFlattenResult(answers, visibility);
  }

  const primaryAnswer = getPrimaryAnswerWithPrepopulation(
    primaryQuestion,
    answers,
  );

  // Map questions with answers and set visibility based on primary answer
  const visibilityByQuestionName = { ...visibility };

  const questionsWithVisibility = filteredQuestions.map(q => {
    visibilityByQuestionName[q.name] =
      primaryAnswer !== null && !isNoCoverValue(primaryAnswer)
        ? !!q.visible
        : false;

    return { ...q, includeAnswer: !!q.visible };
  });

  // Merge primary question and flattened group questions
  return {
    questions: [primaryQuestion, ...questionsWithVisibility],
    groups: {
      [primaryQuestion.name]: {
        kind: "personalised_cover",
        names: questionsWithVisibility.map(q => q.name),
      },
    },
    visibility: visibilityByQuestionName,
    answers: {
      ...answers,
      [primaryQuestion.name]: primaryAnswer || "",
    },
  };
};
